import Question, { QuestionProps } from '@/modules/chatbot/components/Question';
import {
  AMPLITUDE_ACTIONS,
  AMPLITUDE_EVENTS,
} from '@/modules/core/amplitude/events';
import { LogEvent } from '@/modules/core/amplitude/types/Amplitude';
import findNextQuestionToAsk from '@/modules/core/chatbot/findNextQuestionToAsk';
import { ChatbotQuestions } from '@/modules/core/chatbot/getQuestionsToAsk';
import { shouldAnimateCurrentQuestion } from '@/modules/core/chatbot/shouldAnimateCurrentQuestion';
import { AirtableRecord } from '@/modules/core/data/types/AirtableRecord';
import { Campus } from '@/modules/core/data/types/Campus';
import ChatbotQuestion from '@/modules/core/data/types/CDE/ChatbotQuestion';
import UpdatedQuestion from '@/modules/core/data/types/CDE/UpdatedQuestion';
import { ChatbotContext } from '@/modules/core/data/types/ChatbotContext';
import { CustomerTheme } from '@/modules/core/data/types/CustomerTheme';
import { EducationalProgram } from '@/modules/core/data/types/EducationalProgram';
import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import { ModChatbot } from '@/modules/core/data/types/ModChatbot';
import { StudentSolutionsCategory } from '@/modules/core/data/types/StudentSolutionsCategory';
import {
  css,
  useTheme,
} from '@emotion/react';
import Icon from '@/common/components/dataDisplay/Icon';
import { XCircle } from 'lucide-react';
import classnames from 'classnames';
import filter from 'lodash.filter';
import find from 'lodash.find';
import get from 'lodash.get';
import map from 'lodash.map';
import React, {
  Dispatch,
  Fragment,
  SetStateAction,
} from 'react';

type Props = {
  questions: ChatbotQuestions;
  activeQuestion: ChatbotQuestion;
  context: ChatbotContext;
  setActiveQuestion: Dispatch<SetStateAction<ChatbotQuestion>>;
  setContext: Dispatch<SetStateAction<ChatbotContext>>;
  updatedQuestion: UpdatedQuestion;
  setUpdatedQuestion: Dispatch<SetStateAction<UpdatedQuestion>>;
  updatedQuestionAnimationDuration: number;
  logEvent: LogEvent;
  bubbleTimerDuration: number;
  allEducationalPrograms: AirtableRecord<EducationalProgram>[];
  allEducationalProgramSteps: AirtableRecord<EducationalProgramStep>[];
  allCampuses: AirtableRecord<Campus>[];
  allStudentSolutionsCategories: AirtableRecord<StudentSolutionsCategory>[];
  modChatbot: AirtableRecord<ModChatbot>;
}

/**
 * Question about the campuses the user's interested in.
 *
 * Displayed fourth, unless there are no campuses available or the question is deactivated.
 *
 * Although it's "plural", it only allows selecting one campus.
 * That's because we handled plural first but that used to make the UX more complicated and wasn't what our customers wanted (GEM, in particular).
 */
const CampusesQuestion: React.FunctionComponent<Props> = (props): JSX.Element => {
  const {
    questions,
    activeQuestion,
    context,
    setActiveQuestion,
    setContext,
    updatedQuestion,
    setUpdatedQuestion,
    updatedQuestionAnimationDuration,
    logEvent,
    bubbleTimerDuration,
    allEducationalPrograms,
    allEducationalProgramSteps,
    allCampuses,
    allStudentSolutionsCategories,
    modChatbot,
  } = props;
  const {
    campusesQuestion,
  } = questions;
  const theme: CustomerTheme = useTheme();

  return (
    <Question
      question={campusesQuestion}
      activeQuestion={activeQuestion}
      context={context}
      bubbleTimerDuration={bubbleTimerDuration}
    >
      {
        (props: QuestionProps): JSX.Element => {
          const {
            question,
          } = props;

          return (
            <Fragment>
              <div
                className={'message'}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: question.label,
                  }}
                />
                <div
                >
                  {
                    map(filter(question.choices, (choice: Campus): boolean => {
                      // Filter out the selected choice, when a choice is selected
                      return !find(context[question.ref], { ref: choice.ref });
                    }), (choice: Campus, i: number) => {
                      const { label } = choice;

                      return (
                        <button
                          key={i}
                          className={classnames('option-button')}
                          onClick={(): void => {
                            const { ref } = choice;
                            const selectedChoices: Campus[] = [choice];

                            const nextContext: ChatbotContext = {
                              ...context,
                              [question.ref]: selectedChoices,
                            };

                            setActiveQuestion(findNextQuestionToAsk(question, nextContext, questions, allEducationalPrograms, allEducationalProgramSteps, allCampuses, allStudentSolutionsCategories, modChatbot, setContext));

                            // Handling animation of other questions that need to be "refreshed" when this answer changes
                            setUpdatedQuestion(question);
                            setTimeout(() => {
                              setUpdatedQuestion(null);
                            }, updatedQuestionAnimationDuration);

                            logEvent(AMPLITUDE_EVENTS.CHATBOT_ANSWER, {
                              question: {
                                ref: question.ref,
                                inputType: 'quick-replies',
                                isMultiChoices: false,
                                rank: question.order,
                              },
                              selection: {
                                ref: ref,
                              },
                              action: AMPLITUDE_ACTIONS.SELECT,
                            });
                          }}
                          dangerouslySetInnerHTML={{ __html: label }}
                        />
                      );
                    })
                  }
                </div>
              </div>
              {
                context[question.ref] && (
                  <div
                    className={classnames('message user', { [`animated fadeIn delay-100ms duration-${updatedQuestionAnimationDuration}ms`]: shouldAnimateCurrentQuestion(updatedQuestion, question) })}
                  >
                    <span
                      // @ts-ignore
                      dangerouslySetInnerHTML={{ __html: get(find(question.choices, { ref: context[question.ref][0].ref }), 'label') }}
                    />
                    <span
                      role="button"
                      tabIndex={0}
                      className={'close-message'}
                      css={css`
                        cursor: pointer;
                      `}
                      onClick={(): void => {
                        const nextContext: ChatbotContext = {
                          ...context,
                          [question.ref]: undefined,
                        };
                        setActiveQuestion(findNextQuestionToAsk(question, nextContext, questions, allEducationalPrograms, allEducationalProgramSteps, allCampuses, allStudentSolutionsCategories, modChatbot, setContext));

                        logEvent(AMPLITUDE_EVENTS.CHATBOT_ANSWER, {
                          question: {
                            ref: question.ref,
                            inputType: 'quick-replies',
                            isMultiChoices: false,
                            rank: question.order,
                          },
                          selection: {
                            ref: context[question.ref]?.[0]?.ref,
                          },
                          action: AMPLITUDE_ACTIONS.REMOVE,
                        });
                      }}
                    >
                      <Icon icon={XCircle} />
                    </span>
                  </div>
                )
              }
            </Fragment>
          );
        }
      }
    </Question>
  );
};

export default CampusesQuestion;
